import React, { useState, useEffect } from 'react';
import { useDispatch } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import TextareaAutosize from 'react-textarea-autosize';
import { ImageIcon, Video, X } from 'lucide-react';
import ModalOverlay from '../ModalOverlay/ModalOverlay.tsx';
import { openMainModal } from '../../../store/actions/profileActions';
import { generateProfilePostText, createProfilePost } from '../../../store/actions';
import useUserImage from '../../../hooks/useUserImage.js';
import { fireAnalytics } from '../../../utils/firebase';
import { getSessionId, getDeviceCategory } from '../../../utils/analyticsHelpers.ts';
import styles from './ProfilePostSuggestionModal.module.scss';

interface ProfilePostSuggestionModalProps {
  isOpen: boolean;
  onClose: () => void;
  onPublish?: () => void;
  userData?: any;
}

const ProfilePostSuggestionModal: React.FC<ProfilePostSuggestionModalProps> = ({
  isOpen,
  onClose,
  onPublish,
  userData,
}) => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { profileImage, initials, imageError } = useUserImage(userData ?? {});
  const completeName = userData?.type === 'COMPANY'
    ? (userData?.razon_social ?? '')
    : `${userData?.name ?? ''} ${userData?.last_name ?? ''}`.trim() || 'Usuario';
  const [suggestedText, setSuggestedText] = useState<string>('');
  const [editedText, setEditedText] = useState<string>('');
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [isPublishing, setIsPublishing] = useState<boolean>(false);
  const [imageFile, setImageFile] = useState<File | null>(null);
  const [videoFile, setVideoFile] = useState<File | null>(null);
  const [imagePreview, setImagePreview] = useState<string | null>(null);
  const [videoPreview, setVideoPreview] = useState<string | null>(null);
  const [isDragOver, setIsDragOver] = useState(false);


  useEffect(() => {
    if (isOpen && !suggestedText) {
      fetchSuggestedText();
    }
    if (!isOpen) {
      setSuggestedText('');
      setEditedText('');
      setImageFile(null);
      setVideoFile(null);
      setIsDragOver(false);
      setImagePreview((prev) => {
        if (prev) URL.revokeObjectURL(prev);
        return null;
      });
      setVideoPreview((prev) => {
        if (prev) URL.revokeObjectURL(prev);
        return null;
      });
    }
  }, [isOpen]);

  useEffect(() => {
    if (suggestedText && !editedText) {
      setEditedText(suggestedText);
    }
  }, [suggestedText]);

  const getSkillName = (skill: any): string => {
    if (typeof skill === 'string') {
      return skill;
    }
    if (skill?.skill?.name) {
      return skill.skill.name;
    }
    if (skill?.name) {
      return skill.name;
    }
    return '';
  };

  const hasOnlyOtrosAsSkill = (userData: any): boolean => {
    if (!userData?.skills?.length) return false;
    const skillNames = userData.skills.map(getSkillName).filter((s: string) => s && s.trim() !== '');
    return skillNames.length > 0 && skillNames.every((s: string) => s.trim().toLowerCase() === 'otros');
  };

  const getGenericIncompleteProfileText = (): string => {
    return '¡Hola! Estoy en Edify y me encantaría conectarme con profesionales de la construcción.\n\nSi necesitás colaboración en proyectos, sumar gente a tu equipo o conocer otros del rubro, escribime.';
  };

  const generateGenericText = (userData: any): string => {
    if (!userData) {
      return getGenericIncompleteProfileText();
    }
    if (hasOnlyOtrosAsSkill(userData)) {
      return getGenericIncompleteProfileText();
    }

    const isCompany = userData.type === 'COMPANY';
    let name = '';
    if (isCompany) {
      name = userData.razon_social || userData.username || 'Mi empresa';
    } else {
      const firstName = userData.name || '';
      const lastName = userData.last_name || '';
      name = `${firstName} ${lastName}`.trim() || userData.username || 'Yo';
    }
    const description = userData.description || '';
    const skills = userData.skills || [];
    const validSkills = skills
      .map(getSkillName)
      .filter((s: string) => s && s.trim() !== '' && s.trim().toLowerCase() !== 'otros');
    const skillNames = validSkills.slice(0, 3).join(', ');

    let text = `¡Hola! Soy ${name}`;
    if (isCompany) {
      text += ', una empresa';
    } else {
      text += ', un profesional';
    }
    text += '.\n\n';
    if (description && description.trim()) {
      text += `${description.trim()}\n\n`;
    }
    if (skillNames) {
      text += `Me especializo en ${skillNames}`;
      if (validSkills.length > 3) {
        text += ` y más`;
      }
      text += '.\n\n';
    }
    text += 'Estoy en Edify para conectarme con otros profesionales y proyectos. ¡Contáctame si necesitás mis servicios!';
    return text;
  };

  const formatTextWithLineBreaks = (text: string): string => {
    if (!text) return '';
    
    let normalizedText = text.replace(/\r\n/g, '\n').replace(/\r/g, '\n');
    
    if (normalizedText.includes('\n')) {
      return normalizedText;
    }
    
    const sentences = normalizedText.split(/(?<=[.!?])\s+(?=[A-Z¿¡])/);
    
    if (sentences.length <= 2) {
      return normalizedText;
    }
    
    const paragraphs: string[] = [];
    let currentParagraph = '';
    
    sentences.forEach((sentence, index) => {
      const trimmed = sentence.trim();
      if (!trimmed) return;
      
      currentParagraph = currentParagraph ? currentParagraph + ' ' + trimmed : trimmed;
      
      if (index === sentences.length - 1) {
        paragraphs.push(currentParagraph);
      } else if (index > 0 && (index + 1) % 2 === 0) {
        paragraphs.push(currentParagraph);
        currentParagraph = '';
      }
    });
    
    return paragraphs.join('\n\n');
  };

  const fetchSuggestedText = async () => {
    setIsLoading(true);
    try {
      const text = await dispatch(generateProfilePostText() as any);
      const formattedText = formatTextWithLineBreaks(text);
      setSuggestedText(formattedText);
      setEditedText(formattedText);
    } catch (err: any) {
      const genericText = generateGenericText(userData);
      setSuggestedText(genericText);
      setEditedText(genericText);
    } finally {
      setIsLoading(false);
    }
  };

  const handlePublish = async () => {
    if (!editedText.trim()) {
      dispatch(openMainModal({
        title: 'Texto vacío',
        body: 'El texto no puede estar vacío. Escribí algo para publicar.',
        iconType: 'error',
        isSuccess: false,
        confirmHandler: null,
      }));
      return;
    }

    setIsPublishing(true);
    try {
      await dispatch(createProfilePost({
        content: editedText.trim(),
        images: imageFile || '',
        video: videoFile || '',
      }) as any);
      onClose();
      if (onPublish) {
        onPublish();
      } else {
        navigate('/marketplace/explorar');
      }
    } catch (err: any) {
      dispatch(openMainModal({
        title: 'Error al publicar',
        body: 'No pudimos publicar el posteo. Intentá nuevamente en unos minutos.',
        iconType: 'error',
        isSuccess: true,
        confirmHandler: null,
      }));
    } finally {
      setIsPublishing(false);
    }
  };

  const handleCancel = () => {
    fireAnalytics('profile_post_suggestion_cancelled', {
      edify_user_id: userData?.id ?? '',
      session_id: getSessionId(),
      device_category: getDeviceCategory(),
      category: 'publishing',
      action: 'profile_post_suggestion_cancelled',
    });
    setEditedText(suggestedText);
    onClose();
  };

  const handleImageChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) {
      setImageFile(file);
      setImagePreview(URL.createObjectURL(file));
      setVideoFile(null);
      setVideoPreview(null);
    }
  };

  const handleVideoChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) {
      setVideoFile(file);
      setVideoPreview(URL.createObjectURL(file));
      setImageFile(null);
      setImagePreview(null);
    }
  };

  const removeMedia = () => {
    if (imagePreview) URL.revokeObjectURL(imagePreview);
    if (videoPreview) URL.revokeObjectURL(videoPreview);
    setImageFile(null);
    setImagePreview(null);
    setVideoFile(null);
    setVideoPreview(null);
  };

  const handleDragOver = (e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    if (!isPublishing) setIsDragOver(true);
  };

  const handleDragLeave = (e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragOver(false);
  };

  const handleDrop = (e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragOver(false);
    if (isPublishing) return;
    const file = e.dataTransfer.files?.[0];
    if (!file) return;
    if (file.type.startsWith('image/')) {
      setImageFile(file);
      setImagePreview(URL.createObjectURL(file));
      setVideoFile(null);
      setVideoPreview(null);
    } else if (file.type.startsWith('video/')) {
      setVideoFile(file);
      setVideoPreview(URL.createObjectURL(file));
      setImageFile(null);
      setImagePreview(null);
    }
  };

  return (
    <ModalOverlay isOpen={isOpen} onClose={handleCancel} preventClose={isPublishing || isLoading}>
      <div className={styles.modal}>
        <h3 className={styles.title}>Presentate en la plataforma</h3>
        <p className={styles.description}>
          Generamos un texto sugerido para que te presentes. Es editable y muy útil para que otros usuarios te conozcan.
        </p>
        <div className={styles.contentBodyWrapper}>
          <div className={styles.contentBody}>
            {isLoading ? (
              <div className={styles.loadingContainer}>
                <div className={styles.loadingSpinner}></div>
                <p className={styles.loadingText}>Generando texto sugerido...</p>
              </div>
            ) : (
              <div className={styles.postCard}>
                <div className={styles.cardHeader}>
                  <div className={styles.cardAvatar}>
                    {!imageError && profileImage ? (
                      <img src={profileImage} alt="" className={styles.cardAvatar_img} />
                    ) : initials ? (
                      <div className={styles.cardAvatar_initials}>{initials}</div>
                    ) : (
                      <div className={styles.cardAvatar_initials}>U</div>
                    )}
                  </div>
                  <div className={styles.cardHeader_info}>
                    <p className={styles.cardHeader_name}>{completeName}</p>
                    <span className={styles.cardHeader_time}>Ahora</span>
                  </div>
                </div>
                <div className={styles.cardBody}>
                  {imagePreview ? (
                    <div
                      className={`${styles.cardMedia} ${styles.cardMedia_withBlur}`}
                      style={{ '--preview-bg': `url(${imagePreview})` } as React.CSSProperties}
                    >
                      <img src={imagePreview} alt="" className={styles.cardMedia_content} />
                      <button
                        type="button"
                        className={styles.cardMedia_remove}
                        onClick={removeMedia}
                        disabled={isPublishing}
                        aria-label="Quitar foto"
                      >
                        <X size={16} />
                      </button>
                    </div>
                  ) : videoPreview ? (
                    <div className={styles.cardMedia}>
                      <video src={videoPreview} controls className={styles.cardMedia_video} />
                      <button
                        type="button"
                        className={styles.cardMedia_remove}
                        onClick={removeMedia}
                        disabled={isPublishing}
                        aria-label="Quitar video"
                      >
                        <X size={16} />
                      </button>
                    </div>
                  ) : (
                    <div
                      className={`${styles.cardMedia_add} ${isDragOver ? styles.cardMedia_add_dragOver : ''}`}
                      onDragOver={handleDragOver}
                      onDragLeave={handleDragLeave}
                      onDrop={handleDrop}
                    >
                      <p className={styles.cardMedia_addText}>Tu foto o video aparecerá aquí</p>
                      <p className={styles.cardMedia_addHint}>Arrastrá un archivo o elegí una opción. Los posteos con imagen conectan más.</p>
                      <div className={styles.cardMedia_addActions}>
                        <label className={styles.cardMedia_addBtn}>
                          <input type="file" accept="image/*" onChange={handleImageChange} disabled={isPublishing} />
                          <ImageIcon size={24} />
                          <span>Foto</span>
                        </label>
                        <span className={styles.cardMedia_addSep}>o</span>
                        <label className={styles.cardMedia_addBtn}>
                          <input type="file" accept="video/*" onChange={handleVideoChange} disabled={isPublishing} />
                          <Video size={24} />
                          <span>Video</span>
                        </label>
                      </div>
                    </div>
                  )}
                  <div className={styles.cardText}>
                    <TextareaAutosize
                      className={styles.cardTextarea}
                      value={editedText}
                      onChange={(e) => setEditedText(e.target.value)}
                      placeholder="Escribí tu presentación..."
                      minRows={5}
                      disabled={isPublishing}
                    />
                  </div>
                </div>
              </div>
            )}
          </div>
        </div>
        {!isLoading && (
          <div className={styles.modalFooter}>
            <button
              type="button"
              className={styles.cancelButton}
              onClick={handleCancel}
              disabled={isPublishing || isLoading}
            >
              Cancelar
            </button>
            <button
              type="button"
              className={styles.publishButton}
              onClick={handlePublish}
              disabled={isPublishing || isLoading || !editedText.trim()}
            >
              {isPublishing ? 'Publicando...' : 'Publicar'}
            </button>
          </div>
        )}
      </div>
    </ModalOverlay>
  );
};

export default ProfilePostSuggestionModal;
